/**
 * Procedures Dashboard
 *
 * Stat tiles (runs today and this week, completion rate, open problems, due for review, AI questions,
 * published / drafts / public / private) then recent runs, open problems (resolve inline), review due,
 * recent AI questions and the steps where people stall.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconPlayerPlay,
  IconCalendarWeek,
  IconCircleCheck,
  IconFlag,
  IconClock,
  IconMessageCircle,
  IconBroadcast,
  IconPencil,
  IconWorld,
  IconLock,
  IconRefresh,
  IconPlus,
  IconFileImport,
  IconChevronRight,
  IconMoodSmile,
  IconHandStop,
  IconCheck,
} from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { Stack, Group, Button, Text, Title, SimpleGrid, Grid, Alert, Badge, Paper, Textarea, Skeleton, Box } from '@/components/ui';
import ActionIcon from '@/components/ui/ActionIcon';
import { FadeIn } from '@/components/animations';
import { PageHelpButton } from '@/components/help';
import { useProceduresServices, type ProcedureProblemView } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useProceduresPermissions } from '@/hooks/procedures/useProceduresPermissions';
import {
  StatTile,
  StatTilesSkeleton,
  SectionCard,
  InlineEmpty,
  RunStatusBadge,
  RunDetailDrawer,
  proceduresKeys,
  proceduresPaths,
  proceduresErrorMessage,
  relativeStamp,
  formatYmd,
} from '../components';

const badgeStyles = { root: { textTransform: 'none' as const } };

function ProblemRow({ problem, canResolve }: { problem: ProcedureProblemView; canResolve: boolean }) {
  const { proceduresAdmin } = useProceduresServices();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const resolve = useMutation({
    mutationFn: () => proceduresAdmin.resolveProblem(problem.runID, problem.stepID, { resolutionNote: note.trim() || null }),
    onSuccess: () => {
      toast.success('Problem resolved', problem.procedureTitle);
      setOpen(false);
      setNote('');
      queryClient.invalidateQueries({ queryKey: proceduresKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: proceduresKeys.problemsRoot() });
    },
    onError: (err) => toast.error("Couldn't resolve the problem", proceduresErrorMessage(err)),
  });

  return (
    <Paper withBorder radius="md" p="sm">
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap" align="flex-start" gap="xs">
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Box onClick={() => navigate(proceduresPaths.editor(problem.procedureID))} style={{ cursor: 'pointer' }}>
              <Text size="sm" fw={600} lineClamp={1}>
                {problem.procedureTitle}
              </Text>
            </Box>
            <Text size="xs" c="dimmed" lineClamp={1}>
              Step {problem.stepNumber}: {problem.stepTitle}
            </Text>
          </Stack>
          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
            {relativeStamp(problem.reportedAt)}
          </Text>
        </Group>
        {problem.note && (
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {problem.note}
          </Text>
        )}
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Text size="xs" c="dimmed">
            {[problem.runnerName, problem.ministryName].filter(Boolean).join(' · ')}
          </Text>
          {canResolve && !open && (
            <Button size="compact-sm" variant="light" color="green" leftSection={<IconCheck size={14} />} onClick={() => setOpen(true)}>
              Resolve
            </Button>
          )}
        </Group>
        {open && (
          <Stack gap="xs">
            <Textarea placeholder="What was done about it (optional)" value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={2} maxLength={1000} />
            <Group justify="flex-end" gap="xs">
              <Button size="compact-sm" variant="subtle" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button size="compact-sm" color="green" loading={resolve.isPending} onClick={() => resolve.mutate()}>
                Mark resolved
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

export default function ProceduresDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { proceduresAdmin } = useProceduresServices();
  const perms = useProceduresPermissions();
  const [openRun, setOpenRun] = useState<number | null>(null);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: proceduresKeys.dashboard(),
    queryFn: () => proceduresAdmin.getDashboard(),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: proceduresKeys.dashboard() });

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          <Group justify="space-between" wrap="wrap" align="flex-start">
            <div>
              <Title order={2}>Procedures</Title>
              <Text size="sm" c="dimmed">
                Step-by-step procedures people run from a QR code or the portal, and how those runs are going.
              </Text>
            </div>
            <Group gap="sm" data-help-id="procedures.dashboard.actions">
              <ActionIcon variant="subtle" size="lg" onClick={refresh} loading={isFetching && !isLoading} aria-label="Refresh">
                <IconRefresh size={18} />
              </ActionIcon>
              {perms.canImport && (
                <Button variant="light" leftSection={<IconFileImport size={16} />} onClick={() => navigate(proceduresPaths.import)} data-help-id="procedures.dashboard.import">
                  Import with AI
                </Button>
              )}
              {perms.canManage && (
                <Button leftSection={<IconPlus size={16} />} onClick={() => navigate(`${proceduresPaths.list}?new=1`)} data-help-id="procedures.dashboard.new">
                  New procedure
                </Button>
              )}
              <PageHelpButton pageKey="procedures.dashboard" />
            </Group>
          </Group>

          {error && <Alert color="red">{proceduresErrorMessage(error, "Couldn't load the dashboard.")}</Alert>}

          {isLoading ? (
            <Stack gap="md">
              <StatTilesSkeleton count={4} />
              <StatTilesSkeleton count={4} />
              <Skeleton height={300} radius="md" />
            </Stack>
          ) : data ? (
            <>
              <SimpleGrid cols={{ base: 2, md: 3, lg: 6 }} data-help-id="procedures.dashboard.stats">
                <StatTile icon={<IconPlayerPlay size={22} />} color="blue" label="Runs today" value={data.runsToday} onClick={perms.canViewRuns ? () => navigate(proceduresPaths.runs) : undefined} helpId="procedures.dashboard.stat-runs" />
                <StatTile icon={<IconCalendarWeek size={22} />} color="indigo" label="Runs this week" value={data.runsThisWeek} onClick={perms.canViewRuns ? () => navigate(proceduresPaths.runs) : undefined} />
                <StatTile icon={<IconCircleCheck size={22} />} color="green" label="Completion (30 days)" value={`${Math.round(data.completionRate30d)}%`} hint="Finished out of finished or abandoned" />
                <StatTile icon={<IconFlag size={22} />} color="red" label="Open problems" value={data.openProblems} emphasis={data.openProblems > 0} onClick={perms.canViewRuns ? () => navigate(proceduresPaths.problems) : undefined} helpId="procedures.dashboard.stat-problems" />
                <StatTile icon={<IconClock size={22} />} color="orange" label="Due for review" value={data.dueForReview} emphasis={data.dueForReview > 0} onClick={() => navigate(`${proceduresPaths.list}?reviewDue=1`)} helpId="procedures.dashboard.stat-review" />
                <StatTile icon={<IconMessageCircle size={22} />} color="violet" label="AI questions (30 days)" value={data.chatQuestions30d} />
              </SimpleGrid>
              <SimpleGrid cols={{ base: 2, md: 4 }}>
                <StatTile icon={<IconBroadcast size={22} />} color="teal" label="Published" value={data.published} hint={`of ${data.totalProcedures} procedures`} onClick={() => navigate(`${proceduresPaths.list}?status=2`)} />
                <StatTile icon={<IconPencil size={22} />} color="gray" label="Drafts" value={data.drafts} onClick={() => navigate(`${proceduresPaths.list}?status=1`)} />
                <StatTile icon={<IconWorld size={22} />} color="blue" label="Public" value={data.publicCount} hint="QR, no sign-in" onClick={() => navigate(`${proceduresPaths.list}?visibility=1`)} />
                <StatTile icon={<IconLock size={22} />} color="grape" label="Private" value={data.privateCount} hint="Assigned people only" onClick={() => navigate(`${proceduresPaths.list}?visibility=2`)} />
              </SimpleGrid>

              <Grid gutter="lg">
                <Grid.Col span={{ base: 12, lg: 7 }}>
                  <SectionCard
                    icon={<IconPlayerPlay size={18} />}
                    title="Recent runs"
                    description="Click a run to see who ticked what, and when."
                    helpId="procedures.dashboard.recent-runs"
                    actions={
                      perms.canViewRuns ? (
                        <Button variant="subtle" size="compact-sm" rightSection={<IconChevronRight size={14} />} onClick={() => navigate(proceduresPaths.runs)}>
                          All runs
                        </Button>
                      ) : undefined
                    }
                  >
                    {data.recentRuns.length === 0 ? (
                      <InlineEmpty icon={<IconPlayerPlay size={22} />} title="No runs yet" description="Runs show here when someone starts a published procedure." />
                    ) : (
                      <Stack gap={6}>
                        {data.recentRuns.map((r) => (
                          <Paper key={r.runID} withBorder radius="md" p="xs" onClick={() => setOpenRun(r.runID)} style={{ cursor: 'pointer' }}>
                            <Group justify="space-between" wrap="nowrap" gap="sm">
                              <Stack gap={0} style={{ minWidth: 0 }}>
                                <Text size="sm" fw={600} lineClamp={1}>
                                  {r.procedureTitle}
                                </Text>
                                <Text size="xs" c="dimmed" lineClamp={1}>
                                  {[r.runnerName, r.ministryName, `${r.stepsDone} of ${r.stepsTotal} steps`].filter(Boolean).join(' · ')}
                                </Text>
                              </Stack>
                              <Group gap="xs" wrap="nowrap">
                                {r.problemCount > 0 && (
                                  <Badge size="sm" color="red" variant="light" leftSection={<IconFlag size={11} />} styles={badgeStyles}>
                                    {r.problemCount}
                                  </Badge>
                                )}
                                <RunStatusBadge statusId={r.statusID} name={r.statusName} size="sm" />
                                <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                                  {relativeStamp(r.startedAt)}
                                </Text>
                              </Group>
                            </Group>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </SectionCard>
                </Grid.Col>
                <Grid.Col span={{ base: 12, lg: 5 }}>
                  <SectionCard
                    icon={<IconFlag size={18} />}
                    color="red"
                    title="Open problems"
                    description="Reported on a step during a run."
                    helpId="procedures.dashboard.problems"
                    actions={
                      perms.canViewRuns ? (
                        <Button variant="subtle" size="compact-sm" rightSection={<IconChevronRight size={14} />} onClick={() => navigate(proceduresPaths.problems)}>
                          All problems
                        </Button>
                      ) : undefined
                    }
                  >
                    {data.openProblemItems.length === 0 ? (
                      <InlineEmpty icon={<IconMoodSmile size={22} />} title="No open problems" description="Nothing has been reported." />
                    ) : (
                      <Stack gap="xs">
                        {data.openProblemItems.map((p) => (
                          <ProblemRow key={`${p.runID}-${p.stepID}`} problem={p} canResolve={perms.canManage} />
                        ))}
                      </Stack>
                    )}
                  </SectionCard>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                  <SectionCard icon={<IconClock size={18} />} color="orange" title="Due for review" description="Check the steps are still right, then publish again." helpId="procedures.dashboard.review-due">
                    {data.reviewDue.length === 0 ? (
                      <InlineEmpty icon={<IconCircleCheck size={22} />} title="Nothing due" description="Every published procedure is within its review window." />
                    ) : (
                      <Stack gap={6}>
                        {data.reviewDue.map((p) => (
                          <Paper key={p.procedureID} withBorder radius="md" p="xs" onClick={() => navigate(proceduresPaths.editor(p.procedureID))} style={{ cursor: 'pointer' }}>
                            <Group justify="space-between" wrap="nowrap">
                              <Text size="sm" fw={600} lineClamp={1}>
                                {p.title}
                              </Text>
                              <Text size="xs" c="orange" style={{ whiteSpace: 'nowrap' }}>
                                {p.nextReviewDate ? `Due ${formatYmd(p.nextReviewDate)}` : 'Due'}
                              </Text>
                            </Group>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </SectionCard>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                  <SectionCard icon={<IconMessageCircle size={18} />} color="violet" title="Recent AI questions" description="What runners asked the assistant." helpId="procedures.dashboard.questions">
                    {data.recentQuestions.length === 0 ? (
                      <InlineEmpty icon={<IconMessageCircle size={22} />} title="No questions yet" description="Questions show here when runners use the AI chat." />
                    ) : (
                      <Stack gap={6}>
                        {data.recentQuestions.map((q, i) => (
                          <Paper key={i} withBorder radius="md" p="xs" onClick={() => navigate(proceduresPaths.editor(q.procedureID, 'questions'))} style={{ cursor: 'pointer' }}>
                            <Stack gap={2}>
                              <Text size="sm" lineClamp={2}>
                                {q.question}
                              </Text>
                              <Group gap={6} wrap="nowrap">
                                <Text size="xs" c="dimmed" lineClamp={1} style={{ flex: 1 }}>
                                  {q.procedureTitle} · {relativeStamp(q.askedAt)}
                                </Text>
                                {q.answered === false && (
                                  <Badge size="xs" color="orange" variant="light" styles={badgeStyles}>
                                    Not answered
                                  </Badge>
                                )}
                              </Group>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </SectionCard>
                </Grid.Col>
                <Grid.Col span={{ base: 12, lg: 4 }}>
                  <SectionCard icon={<IconHandStop size={18} />} color="yellow" title="Where people stall" description="Steps with problems or where runs were given up (90 days)." helpId="procedures.dashboard.stall">
                    {data.stallSteps.length === 0 ? (
                      <InlineEmpty icon={<IconMoodSmile size={22} />} title="No sticking points" description="Nobody is getting stuck on a particular step." />
                    ) : (
                      <Stack gap={6}>
                        {data.stallSteps.map((s) => (
                          <Paper key={`${s.procedureID}-${s.stepNumber}`} withBorder radius="md" p="xs" onClick={() => navigate(proceduresPaths.editor(s.procedureID, 'steps'))} style={{ cursor: 'pointer' }}>
                            <Stack gap={2}>
                              <Text size="sm" fw={600} lineClamp={1}>
                                Step {s.stepNumber}: {s.stepTitle}
                              </Text>
                              <Group gap={6} wrap="wrap">
                                <Text size="xs" c="dimmed" lineClamp={1}>
                                  {s.procedureTitle}
                                </Text>
                                {s.abandonCount > 0 && (
                                  <Badge size="xs" color="gray" variant="light" styles={badgeStyles}>
                                    {s.abandonCount} gave up here
                                  </Badge>
                                )}
                                {s.problemCount > 0 && (
                                  <Badge size="xs" color="red" variant="light" styles={badgeStyles}>
                                    {s.problemCount} problem{s.problemCount === 1 ? '' : 's'}
                                  </Badge>
                                )}
                              </Group>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </SectionCard>
                </Grid.Col>
              </Grid>
            </>
          ) : null}
        </Stack>
      </FadeIn>
      <RunDetailDrawer runId={openRun} onClose={() => setOpenRun(null)} />
    </LayoutContainer>
  );
}
